import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";
import useLayoutStore from "../../store/useLayoutStore";
import CustomSearch from "../../components/common/CustomSearch";
import CustomButton from "../../components/common/CustomButton";
import axios from "axios";
import config from "../../config";
import CustomModal from "../../components/CustomModal";
import AddAlbumModal from "./AddAlbumModal";
import UpdateAlbumModal from "./UpdateAlbumModal";

const drawerWidth = 240;
const collapsedWidth = 80;

const Album = () => {
  const { isSidebarOpen } = useLayoutStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openImagesModal, setOpenImagesModal] = useState(false);
  const [selectedAlbumImages, setSelectedAlbumImages] = useState([]);
  const [activeImage, setActiveImage] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedAlbumId, setSelectedAlbumId] = useState(null);
  const [selectedAlbumSlug, setSelectedAlbumSlug] = useState(null);

  // Thêm hàm xử lý chỉnh sửa album
  const handleEditAlbum = (albumId, albumSlug) => {
    console.log("Chỉnh sửa album với ID:", albumId, "và Slug:", albumSlug);
    setSelectedAlbumId(albumId);
    setSelectedAlbumSlug(albumSlug);
    setOpenEditModal(true);
  };

  // Thêm hàm xử lý xóa album
  const handleDeleteAlbum = async (albumId) => {
    // Hiển thị hộp thoại xác nhận
    if (!window.confirm("Bạn có chắc chắn muốn xóa album này?")) {
      return;
    }
    
    try {
      setLoading(true);
      // Gọi API xóa album
      const response = await axios.delete(`${config.apiBaseUrl}/Album/delete/${albumId}`);
      
      if (response.status === 200) {
        // Xóa thành công, cập nhật lại danh sách album
        alert("✅ Xóa album thành công!");
        fetchAlbums(); // Gọi lại hàm fetchAlbums để cập nhật danh sách
      } else {
        alert("❌ Có lỗi xảy ra khi xóa album.");
      }
    } catch (error) {
      console.error("❌ Lỗi khi xóa album:", error);
      if (error.response) {
        alert(`❌ Lỗi API: ${error.response.status} - ${error.response.data?.Message || "Không xác định"}`);
      } else {
        alert("❌ Không thể kết nối đến server!");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch albums data
  const fetchAlbums = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.apiBaseUrl}/Album/all`);
      setAlbums(
        response.data.map((item) => ({
          id: item.albumId,
          title: item.title,
          description: item.shortDescription,
          coverImage: item.coverImageUrl,
          imageCount: item.imageUrls?.length || 0,
          createdAt: item.createdAt ? item.createdAt.split("T")[0] : "",
          slug: item.slug,
        }))
      );
    } catch (err) {
      setError(err.message);
      console.error("Error fetching albums:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  // Filter albums by search query
  const filteredAlbums = albums.filter((album) =>
    album.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewImages = async (albumSlug) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/Album/by-slug/${albumSlug}`
      );
      // Lấy danh sách ảnh kiểu mới: mảng object {albumImageId, imageUrl}
      const images = response.data.images || [];
      setSelectedAlbumImages(images);
      setActiveImage(images[0] || null);
      setOpenImagesModal(true);
    } catch (err) {
      setError("Không thể tải ảnh của album này!");
      setSelectedAlbumImages([]);
      setActiveImage(null);
      setOpenImagesModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 2,
        width: `calc(100vw - ${isSidebarOpen ? drawerWidth : collapsedWidth}px)`,
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Albums
        </Typography>
        <CustomButton
          text="+ Add Album"
          onClick={() => setOpenAddModal(true)}
          customColor="#1976d2"
        />
      </Box>

      {/* Search */}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}
      >
        <CustomSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </Box>

      {/* Display Albums */}
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ textAlign: "center", mt: 4 }}>
            {error}
          </Typography>
        ) : filteredAlbums.length === 0 ? (
          <Typography sx={{ textAlign: "center", mt: 4 }}>
            Không tìm thấy album nào.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {filteredAlbums.map((album) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={album.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="140"
                    image={album.coverImage}
                    alt={album.title}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h6" component="div">
                      {album.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {album.description}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {album.imageCount} ảnh • Tạo ngày {album.createdAt}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Tooltip title="Xem ảnh">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewImages(album.slug)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Chỉnh sửa">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEditAlbum(album.id, album.slug)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Xóa album">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDeleteAlbum(album.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      {/* Modal xem ảnh */}
      {openImagesModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setOpenImagesModal(false)}
        >
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: 2,
              p: 3,
              maxWidth: 900,
              maxHeight: "90vh",
              overflow: "auto",
              minWidth: 320,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" mb={2}>
              Ảnh trong Album
            </Typography>
            {selectedAlbumImages.length === 0 ? (
              <Typography>Không có ảnh nào trong album này.</Typography>
            ) : (
              <>
                {/* Ảnh nhỏ dạng lưới */}
                <Grid container spacing={2}>
                  {selectedAlbumImages.map((img, idx) => (
                    <Grid item xs={6} sm={4} md={3} key={img.albumImageId || idx}>
                      <img
                        src={img.imageUrl}
                        alt={`Ảnh ${idx + 1}`}
                        style={{
                          width: "100%",
                          borderRadius: 8,
                          border:
                            (activeImage && (activeImage.albumImageId === img.albumImageId))
                              ? "2px solid #1976d2"
                              : "2px solid transparent",
                          cursor: "pointer",
                          boxSizing: "border-box",
                        }}
                        onClick={() => setActiveImage(img)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
        </Box>
      )}
      {/* Modal thêm album mới */}
      <AddAlbumModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSave={fetchAlbums}
      />
      {/* Modal chỉnh sửa album */}
      <UpdateAlbumModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        albumId={selectedAlbumId}
        albumSlug={selectedAlbumSlug}
        onSave={fetchAlbums}
      />
    </Box>
  );
};

export default Album;

